import { Moon, Sun } from "lucide-react";
import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { meKey, useUpdateProfile } from "@/hooks/useMe";
import type { Me } from "@/lib/types";
import { applyTheme } from "@/hooks/useTheme";
import { cn } from "@/lib/utils";
import { t } from "@shared/i18n";

const isDark = () => document.documentElement.classList.contains("dark");

/** Bascule clair / sombre en un geste ; enregistrée dans le profil si l'utilisateur est connecté. */
export function ThemeToggle({ className }: { className?: string }) {
  // Lecture passive du compte : ne déclenche aucune requête (sinon boucle sur les pages publiques).
  const qc = useQueryClient();
  const update = useUpdateProfile({ silent: true });
  const [dark, setDark] = useState(isDark);
  useEffect(() => {
    const obs = new MutationObserver(() => setDark(isDark()));
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    return () => obs.disconnect();
  }, []);
  const toggle = () => {
    const next = dark ? "light" : "dark";
    applyTheme(next);
    if (qc.getQueryData<Me>(meKey)) update.mutate({ theme: next });
  };
  return (
    <button onClick={toggle} className={cn("press relative flex size-10 items-center justify-center rounded-full text-muted hover:bg-surface hover:text-fg", className)} aria-label={dark ? t("Passer en mode clair") : t("Passer en mode sombre")}>
      <Sun className={cn("absolute size-5 transition-all duration-500", dark ? "rotate-90 scale-0 opacity-0" : "rotate-0 scale-100 opacity-100")} />
      <Moon className={cn("absolute size-5 transition-all duration-500", dark ? "rotate-0 scale-100 opacity-100" : "-rotate-90 scale-0 opacity-0")} />
    </button>
  );
}
